/**
 * CRM Import Utilities
 *
 * Handles parsing and transformation of CRM CSV exports for import into the system.
 */

/**
 * Decodes Unicode escape sequences (\uXXXX) and common HTML entities in a string.
 * Some CRM systems export CSVs with these sequences in text fields.
 */
export function decodeUnicodeEscapes(value: string): string {
  return value
    .replace(/\\u([0-9a-fA-F]{4})/g, (_, code) => String.fromCharCode(parseInt(code, 16)))
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

// Indicator columns that identify a CRM export format
const CRM_INDICATOR_COLUMNS = [
  'primary_email',
  'primary_phone',
  'type',
  'rank',
  'status',
  'remarketing',
  'entity_type',
] as const;

/**
 * Detects if CSV columns match the CRM export schema.
 * Returns true if 4 or more indicator columns are present.
 */
export function detectCrmSchema(columns: string[]): boolean {
  const normalizedColumns = columns.map((col) => col.toLowerCase().trim());
  const matchCount = CRM_INDICATOR_COLUMNS.filter((indicator) =>
    normalizedColumns.includes(indicator)
  ).length;
  return matchCount >= 4;
}

/**
 * Parses a JSON array field from a CSV cell.
 * Handles empty values and malformed JSON gracefully.
 *
 * IMPORTANT: Do NOT blindly replace single quotes with double quotes.
 * Text fields (notes, descriptions) contain apostrophes (don't, isn't, it's)
 * which would break JSON parsing if converted to double quotes.
 * Instead, try parsing as-is first (standard JSON uses double quotes),
 * then fall back to single-quote-delimited JSON only for structural quotes.
 */
export function parseCrmJsonField<T>(value: string): T[] {
  if (!value || value.trim() === '' || value === 'null' || value === 'NULL') {
    return [];
  }

  // Try parsing as standard JSON first (most common case)
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed as T[];
    if (parsed !== null && parsed !== undefined) return [parsed] as T[];
    return [];
  } catch {
    // Standard JSON parse failed
  }

  // Fallback: try replacing only structural single quotes (keys/values delimiters)
  // This handles cases where the CRM exported JSON with single quotes instead of double
  // Pattern: replace ' that appear as JSON structural delimiters, not apostrophes in text
  try {
    // Replace single-quoted JSON: {'key': 'value'} → {"key": "value"}
    // Only replace ' that are adjacent to JSON structural characters: [{,}]:
    const normalized = value
      .replace(/(?<=[\[{,:])\s*'/g, ' "')    // opening quotes after structural chars
      .replace(/'\s*(?=[,\]}:])/g, '"');       // closing quotes before structural chars
    const parsed = JSON.parse(normalized);
    if (Array.isArray(parsed)) return parsed as T[];
    if (parsed !== null && parsed !== undefined) return [parsed] as T[];
    return [];
  } catch {
    // Both attempts failed - return empty array
    return [];
  }
}

// Phone entry from CRM export
export interface CrmPhone {
  number: string;
  type?: string;
  is_primary?: boolean;
}

// Email entry from CRM export
export interface CrmEmail {
  email: string;
  type?: string;
  is_primary?: boolean;
}

// Address entry from CRM export
export interface CrmAddress {
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  type?: string;
  is_primary?: boolean;
}

// URL entry from CRM export
export interface CrmUrl {
  url: string;
  type?: string;
}

// Note entry from CRM export
// CSV format uses {id, text}, legacy format uses {content}
export interface CrmNote {
  content?: string;
  text?: string;
  id?: string;
  created_at?: string;
  type?: string;
}

// Important date entry from CRM export
export interface CrmImportantDate {
  date: string;
  type: string;
  description?: string;
}

// Group entry from CRM export
export interface CrmGroup {
  name: string;
  id?: string;
}

// Interest entry from CRM export (can be {name: string} or plain string)
export interface CrmInterest {
  name?: string;
  id?: string;
}

// Social connection entry from CRM export
export interface CrmConnection {
  field: string;
  value: string;
}

// Custom field entry from CRM export
export interface CrmCustomField {
  name: string;
  value: string;
  type?: string;
}

/**
 * Contact structure as parsed from CRM CSV export
 */
export interface CrmContact {
  // Simple string fields
  first_name: string;
  last_name: string;
  company: string;
  job_title: string;
  industry: string;
  entity_type: string;
  primary_phone: string;
  primary_email: string;
  type: string;
  rank: string;
  status: string;
  relationship: string;
  remarketing: string;
  timezone: string;
  contact_id: string;

  // Array fields (parsed from JSON)
  phones: CrmPhone[];
  emails: CrmEmail[];
  addresses: CrmAddress[];
  urls: CrmUrl[];
  notes: CrmNote[];
  important_dates: CrmImportantDate[];
  groups: CrmGroup[];
  interests: CrmInterest[];
  connections: CrmConnection[];
  custom_fields: CrmCustomField[];
  // Additional columns from Connect export
  activity_notes: { date: string; text: string }[];
  calendar_events: { id?: number; title: string; description?: string; start: string; end?: string }[];
}

/**
 * Parses a single CSV row into a CrmContact structure.
 * Handles missing fields gracefully with empty defaults.
 */
export function parseCrmCsvRow(row: Record<string, string>): CrmContact {
  // Helper to get string value with case-insensitive key lookup
  const getString = (key: string): string => {
    const lowerKey = key.toLowerCase();
    for (const [k, v] of Object.entries(row)) {
      if (k.toLowerCase() === lowerKey) {
        return decodeUnicodeEscapes(v?.trim() ?? '');
      }
    }
    return '';
  };

  return {
    // Simple string fields
    first_name: getString('first_name'),
    last_name: getString('last_name'),
    company: getString('company'),
    job_title: getString('job_title'),
    industry: getString('industry'),
    entity_type: getString('entity_type'),
    primary_phone: getString('primary_phone'),
    primary_email: getString('primary_email'),
    type: getString('type'),
    rank: getString('rank'),
    status: getString('status'),
    relationship: getString('relationship'),
    remarketing: getString('remarketing'),
    timezone: getString('timezone'),
    contact_id: getString('contact_id'),

    // Array fields - parse JSON
    phones: parseCrmJsonField<CrmPhone>(getString('phones')),
    emails: parseCrmJsonField<CrmEmail>(getString('emails')),
    addresses: parseCrmJsonField<CrmAddress>(getString('addresses')),
    urls: parseCrmJsonField<CrmUrl>(getString('urls')),
    notes: parseCrmJsonField<CrmNote>(getString('notes')),
    important_dates: parseCrmJsonField<CrmImportantDate>(getString('important_dates')),
    groups: parseCrmJsonField<CrmGroup>(getString('groups')),
    interests: parseCrmJsonField<CrmInterest>(getString('interests')),
    connections: parseCrmJsonField<CrmConnection>(getString('connections')),
    custom_fields: parseCrmJsonField<CrmCustomField>(getString('custom_fields')),
    activity_notes: parseCrmJsonField<{ date: string; text: string }>(getString('activity_notes')),
    calendar_events: parseCrmJsonField<{ id?: number; title: string; description?: string; start: string; end?: string }>(getString('calendar_events')),
  };
}

// Phone entry for import
// Note: uses 'label' to match edge function's expected field name
export interface ImportPhone {
  phone: string;
  label?: string;
  is_primary?: boolean;
}

// Email entry for import
// Note: uses 'label' to match edge function's expected field name
export interface ImportEmail {
  email: string;
  label?: string;
  is_primary?: boolean;
}

// Address entry for import
// Note: uses 'label' and 'zip' to match edge function's expected field names
export interface ImportAddress {
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  label?: string;
  is_primary?: boolean;
}

// Website entry for import
export interface ImportWebsite {
  url: string;
  label?: string;
}

// Note entry for import
// Note: uses 'note' to match edge function's expected field name
export interface ImportNote {
  note: string;
  created_at?: string;
}

// Date entry for import
export interface ImportDate {
  date: string;
  type: string;
  description?: string;
}

// Group entry for import
export interface ImportGroup {
  name: string;
}

// Custom field entry for import
export interface ImportCustomField {
  name: string;
  value: string;
  type?: string;
}

// Interest entry for import
export interface ImportInterest {
  interest: string;
}

/**
 * Contact structure expected by the edge function import endpoint
 */
export interface ContactImportData {
  first_name: string;
  last_name: string;
  company: string;
  job_title: string;
  type: string;
  rank: string;
  status: string;
  relationship: string;
  industry: string;
  timezone_offset: number | null;
  source_contact_id: string;
  is_company: boolean;
  remarket: boolean;

  // Array fields
  emails: ImportEmail[];
  phones: ImportPhone[];
  addresses: ImportAddress[];
  notes: ImportNote[];
  websites: ImportWebsite[];
  dates: ImportDate[];
  groups: ImportGroup[];
  interests: ImportInterest[];
  custom_fields: ImportCustomField[];
  activity_notes: { date: string; text: string }[];
  calendar_events: { title: string; description?: string; start: string; end?: string }[];
}

/**
 * Parses a timezone string to a numeric offset.
 * Handles formats like "UTC-5", "GMT+8", "-05:00", "+8", etc.
 * Returns null if parsing fails.
 */
function parseTimezoneToOffset(timezone: string): number | null {
  if (!timezone || timezone.trim() === '') {
    return null;
  }

  const trimmed = timezone.trim().toUpperCase();

  // Handle "UTC" or "GMT" alone
  if (trimmed === 'UTC' || trimmed === 'GMT') {
    return 0;
  }

  // Match patterns like "UTC-5", "GMT+8", "UTC-05:00", "GMT+08:30"
  const utcGmtMatch = trimmed.match(/^(?:UTC|GMT)([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (utcGmtMatch) {
    const sign = utcGmtMatch[1] === '+' ? 1 : -1;
    const hours = parseInt(utcGmtMatch[2], 10);
    const minutes = utcGmtMatch[3] ? parseInt(utcGmtMatch[3], 10) : 0;
    return sign * (hours + minutes / 60);
  }

  // Match patterns like "-05:00", "+8", "-5"
  const offsetMatch = trimmed.match(/^([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (offsetMatch) {
    const sign = offsetMatch[1] === '+' ? 1 : -1;
    const hours = parseInt(offsetMatch[2], 10);
    const minutes = offsetMatch[3] ? parseInt(offsetMatch[3], 10) : 0;
    return sign * (hours + minutes / 60);
  }

  // Try parsing as a simple number
  const numericOffset = parseFloat(timezone);
  if (!isNaN(numericOffset) && numericOffset >= -12 && numericOffset <= 14) {
    return numericOffset;
  }

  return null;
}

/**
 * Transforms a CrmContact to ContactImportData format.
 * Handles merging primary email/phone, type conversions, and field mappings.
 *
 * IMPORTANT: Field names must match what the edge function expects:
 * - phones use 'phone' and 'label' (not 'number' and 'type')
 * - emails use 'email' and 'label' (not 'type')
 * - addresses use 'zip' and 'label' (not 'postal_code' and 'type')
 * - notes use 'note' (not 'content')
 */
export function transformCrmContactToImport(crm: CrmContact): ContactImportData {
  // Build emails array, converting CrmEmail to ImportEmail format
  // CrmEmail has: { email, type } -> ImportEmail needs: { email, label }
  const emails: ImportEmail[] = crm.emails.map((e) => ({
    email: e.email,
    label: e.type || 'work',
    is_primary: e.is_primary,
  }));

  // Merge primary_email if present
  if (crm.primary_email && crm.primary_email.trim() !== '') {
    const primaryEmailLower = crm.primary_email.toLowerCase().trim();
    const existingIndex = emails.findIndex(
      (e) => e.email.toLowerCase().trim() === primaryEmailLower
    );

    if (existingIndex >= 0) {
      // Mark existing as primary, preserve its label
      emails[existingIndex] = { ...emails[existingIndex], is_primary: true };
    } else {
      // Add primary email at the beginning with default label
      emails.unshift({
        email: crm.primary_email.trim(),
        label: 'work',
        is_primary: true,
      });
    }
  }

  // Build phones array, converting CrmPhone to ImportPhone format
  // CrmPhone has: { number, type } -> ImportPhone needs: { phone, label }
  const phones: ImportPhone[] = crm.phones.map((p) => ({
    phone: p.number,
    label: p.type || 'main',
    is_primary: p.is_primary,
  }));

  // Merge primary_phone if present
  if (crm.primary_phone && crm.primary_phone.trim() !== '') {
    // Normalize phone for comparison (remove non-digits for matching)
    const normalizePhone = (phone: string) => phone.replace(/\D/g, '');
    const primaryPhoneNormalized = normalizePhone(crm.primary_phone);
    const existingIndex = phones.findIndex(
      (p) => normalizePhone(p.phone) === primaryPhoneNormalized
    );

    if (existingIndex >= 0) {
      // Mark existing as primary, preserve its label
      phones[existingIndex] = { ...phones[existingIndex], is_primary: true };
    } else {
      // Add primary phone at the beginning with default label
      phones.unshift({
        phone: crm.primary_phone.trim(),
        label: 'main',
        is_primary: true,
      });
    }
  }

  // Convert entity_type to is_company boolean
  const isCompany =
    crm.entity_type.toLowerCase() === 'company' ||
    crm.entity_type.toLowerCase() === 'organization';

  // Convert remarketing YES/NO to boolean
  const remarketingLower = crm.remarketing.toLowerCase().trim();
  const remarket =
    remarketingLower === 'yes' ||
    remarketingLower === 'true' ||
    remarketingLower === '1';

  // Parse timezone to numeric offset
  const timezoneOffset = parseTimezoneToOffset(crm.timezone);

  // Map urls to websites (type -> label)
  const websites: ImportWebsite[] = crm.urls.map((u) => ({
    url: u.url,
    label: u.type || 'work',
  }));

  // Map important_dates to dates
  const dates: ImportDate[] = crm.important_dates.map((d) => ({
    date: d.date,
    type: d.type,
    description: d.description,
  }));

  // Map notes (accepts both 'content' and 'text' field names from different CRM exports)
  const notes: ImportNote[] = crm.notes
    .filter((n) => n.content || n.text)
    .map((n) => ({
      note: n.content || n.text || '',
      created_at: n.created_at,
    }));

  // Map addresses (postal_code -> zip, type -> label)
  const addresses: ImportAddress[] = crm.addresses.map((a) => ({
    street: a.street,
    street2: a.street2,
    city: a.city,
    state: a.state,
    zip: a.postal_code,
    country: a.country,
    label: a.type || 'main',
    is_primary: a.is_primary,
  }));

  // Map groups (handle both {name: string} objects and plain strings from parseCrmJsonField)
  const groups: ImportGroup[] = crm.groups
    .map((g) => ({
      name: typeof g === 'string' ? g : (g.name || ''),
    }))
    .filter((g) => g.name.trim() !== '');

  // Map interests (handle both {name: string} objects and plain strings from parseCrmJsonField)
  const interests: ImportInterest[] = crm.interests
    .map((i) => ({
      interest: typeof i === 'string' ? i : (i.name || ''),
    }))
    .filter((i) => i.interest.trim() !== '');

  // Map custom fields
  const customFields: ImportCustomField[] = crm.custom_fields.map((cf) => ({
    name: cf.name,
    value: cf.value,
    type: cf.type,
  }));

  // Merge connections (social links) into custom fields
  crm.connections.forEach((conn) => {
    if (conn.field && conn.value) {
      customFields.push({
        name: conn.field,
        value: conn.value,
      });
    }
  });

  return {
    first_name: crm.first_name,
    last_name: crm.last_name,
    company: crm.company,
    job_title: crm.job_title,
    type: crm.type,
    rank: crm.rank,
    status: crm.status || 'Not Yet Contacted',
    relationship: crm.relationship,
    industry: crm.industry,
    timezone_offset: timezoneOffset,
    source_contact_id: crm.contact_id,
    is_company: isCompany,
    remarket: remarket,
    emails,
    phones,
    addresses,
    notes,
    websites,
    dates,
    groups,
    interests,
    custom_fields: customFields,
    activity_notes: crm.activity_notes.filter((a) => a.text?.trim()),
    calendar_events: crm.calendar_events.filter((e) => e.title?.trim()),
  };
}
